import React, { useEffect, useState } from "react";
import { GridColDef } from "@mui/x-data-grid";
import { FormViewType } from "../../Tickets/Tickets";
import { FieldErrors } from "../../../../domain/helpers/Errors";
import { darkModeBackgroud } from "../../../../utils/constants";
import { DataSelectType } from "../../../../models/DataSelectType";
import { AssetInterface } from "../../../../models/AssetModel/Asset";
import { useLanguageStore } from "../../../../store/useLanguageStore";
import { useDarkModeStore } from "../../../../store/useDarkModeStore";
import FilterBtn from "../Components/Header/FilterBtn";
import StatusBadge from "../../../../common/StatusBadge";
import ToastMessage from "../../../../common/ToastMessage";
import CustomDataGrid from "../../../../common/CustomDataGrid";
import LayoutSelect from "../../Tickets/Components/TableForm/LayoutSelect";
import useAssets from "../../../../store/ReactQueries/assets/asset/useAsset";
import FilterAssetComponent from "../Components/Filter/FilterAssetComponent";
import useDeleteAsset from "../../../../store/ReactQueries/assets/asset/useDeleteAsset";
import dayjs from "dayjs";

interface TableViewFormProps {
  page: number;
  pageSize: number;
  formView: FormViewType;
  form: AssetInterface;
  showFilters: boolean;
  filterData: DataSelectType[] | undefined;
  setPage: React.Dispatch<React.SetStateAction<number>>;
  setPageSize: React.Dispatch<React.SetStateAction<number>>;
  setShowFilters: React.Dispatch<React.SetStateAction<boolean>>;
  setForm: React.Dispatch<React.SetStateAction<AssetInterface>>;
  setFormView: React.Dispatch<React.SetStateAction<FormViewType>>;
  setFilterData: React.Dispatch<React.SetStateAction<DataSelectType[] | undefined>>;
}

export const TableViewForm: React.FC<TableViewFormProps> = ({
  page,
  form,
  pageSize,
  formView,
  filterData,
  showFilters,
  setForm,
  setPage,
  setPageSize,
  setFormView,
  setFilterData,
  setShowFilters,
}) => {
  const { darkMode } = useDarkModeStore();
  const { language } = useLanguageStore();
  let { data: assets, isLoading, error: ticketErrors } = useAssets(page, pageSize, filterData);

  const [errors, setErrors] = useState<FieldErrors>({});
  const deleteAsset = useDeleteAsset(page, pageSize);

  useEffect(() => {
    if (ticketErrors) ToastMessage.Failed(ticketErrors.message);
  }, [ticketErrors]);

  const columns: GridColDef[] = [
    {
      field: "id",
      headerName: "Id",
      type: "string",
      editable: false,
    },
    {
      field: "barcode",
      headerName: "Barcode",
      type: "string",
      editable: false,
    },
    {
      field: "title",
      headerName: "Title",
      type: "string",
      editable: false,
      width: 400,
    },
    {
      field: "assetCategory",
      headerName: "AssetCategory",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.assetCategory ? `${row.assetCategory?.title ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "assetType",
      headerName: "AssetType",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.assetType ? `${row.assetType?.title ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "description",
      headerName: "Description",
      type: "string",
      editable: false,
    },
    {
      field: "assetDepartment",
      headerName: "AssetDepartment",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.assetDepartment ? `${row.assetDepartment?.title ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "price",
      headerName: "Price",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.price ? `${row.price.toFixed(2)}€` : `0.00€`}</span>,
    },
    {
      field: "company",
      headerName: "Company",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.company ? `${row.company?.title ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "location",
      headerName: "Location",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.location ? `${row.location?.title ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "customer",
      headerName: "Customer",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.customer ? `${row.customer?.title ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "vendor",
      headerName: "Vendor",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.vendor ? `${row.vendor?.name ?? ""}` : ""}</span>,
      width: 150,
    },
    {
      field: "assetCategoryId",
      headerName: "AssetCategoryId",
      type: "string",
      editable: false,
    },
    {
      field: "assetTypeId",
      headerName: "AssetType",
      type: "string",
      editable: false,
    },
    {
      field: "assetDepartmentId",
      headerName: "AssetDepartmentId",
      type: "string",
      editable: false,
    },
    {
      field: "assetStatusId",
      headerName: "AssetStatusId",
      type: "string",
      editable: false,
    },
    {
      field: "companyId",
      headerName: "CompanyId",
      type: "string",
      editable: false,
    },
    {
      field: "customerId",
      headerName: "CustomerId",
      type: "string",
      editable: false,
    },
    {
      field: "locationId",
      headerName: "LocationId",
      type: "string",
      editable: false,
    },
    {
      field: "assetStatus",
      headerName: "AssetStatus",
      type: "string",
      editable: false,
      cellClassName: "d-flex align-items-center",
      width: 200,
      renderCell: ({ row }) => (
        <StatusBadge
          name={row.assetStatus ? row.assetStatus.title : "No Status"}
          color={row.assetStatus?.badgeColor || "#8D97A0"}
          width={"fit-content"}
          fontSize={12}
        />
      ),
    },
    {
      field: "boughtDate",
      headerName: "Bought Date",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.boughtDate ? dayjs(row.boughtDate).format("DD/MM/YYYY") : ""}</span>,
      width: 120,
    },
    {
      field: "warrantyDate",
      headerName: "Warranty Date",
      type: "string",
      editable: false,
      renderCell: ({ row }) => <span>{row.boughtDate ? dayjs(row.warrantyDate).format("DD/MM/YYYY") : ""}</span>,
      width: 120,
    },
    {
      field: "invoiceNo",
      headerName: "Invoice No",
      type: "string",
      editable: false,
    },
  ];

  const handleDelete = async (id: string) => {
    if (await ToastMessage.Confirm(language["ToastDeleteItem"])) {
      deleteAsset.mutate(id, {
        onSuccess: () => ToastMessage.Success(),
        onError: (error) => {
          if (error instanceof Error) ToastMessage.Failed(error.message);
          else ToastMessage.Failed();
        },
      });
    }
  };

  const hideColumns = [
    "id",
    "assetCategoryId",
    "assetTypeId",
    "assetDepartmentId",
    "assetStatusId",
    "companyId",
    "customerId",
    "locationId",
    "assetType",
    "description",
    "assetDepartment",
  ];

  return (
    <React.Fragment>
      <div className="w-100 p-2 d-flex align-items-center">
        <div className="ms-auto d-flex flex-row gap-1">
          <LayoutSelect size="small" formView={formView} setFormView={setFormView} module="Assets" />
          {setShowFilters && <FilterBtn showFilters={showFilters ?? false} setShowFilters={setShowFilters} />}
        </div>
      </div>
      <div className={`d-flex flex-row transition-wrapper ${!showFilters && "gap-0"}`}>
        <div className={`content ${showFilters ? "shrink" : ""}`}>
          <CustomDataGrid
            name={language["Assets"]}
            form={form}
            errors={errors}
            columns={columns}
            rows={assets || []}
            newAddComponent="Assets"
            hideColumns={hideColumns}
            showColumnModal={[]}
            addItems={() => {}}
            handleSave={function (): Promise<void> {
              throw new Error("Function not implemented.");
            }}
            modal={{
              add: false,
              edit: false,
              view: false,
            }}
            setModal={function (
              value: React.SetStateAction<{
                add: boolean;
                edit: boolean;
                view: boolean;
              }>
            ): void {
              throw new Error("Function not implemented.");
            }}
            page={page}
            loading={isLoading}
            pageSize={pageSize}
            setPage={setPage}
            setForm={setForm}
            setErrors={setErrors}
            setPageSize={setPageSize}
            handleDelete={handleDelete}
          />
        </div>
        <div
          className={`filter-section ${showFilters ? "expand" : ""}`}
          style={{
            background: darkMode ? darkModeBackgroud : "white",
            border: showFilters ? "1px solid rgba(224, 224, 224, 1)" : "none",
          }}
        >
          {showFilters && (
            <FilterAssetComponent
              page={page}
              pageSize={pageSize}
              filterData={filterData}
              showFilters={showFilters}
              setPage={setPage}
              setFilterData={setFilterData}
            />
          )}
        </div>
      </div>
    </React.Fragment>
  );
};
export default TableViewForm;
